import React from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Badge,
  Tooltip,
  Switch,
  IconButton,
  useTheme,
  alpha,
  Divider,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import RefreshIcon from "@mui/icons-material/Refresh";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ScaleIcon from "@mui/icons-material/Scale";
import TuneIcon from "@mui/icons-material/Tune";
import CalculateIcon from "@mui/icons-material/Calculate";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import { Link } from "react-router-dom";

const ValuePageHeader = ({
  autoRefresh,
  onAutoRefreshToggle,
  onOpenKellyModal,
  onOpenBookieWeightsModal,
  onRefresh,
  backgroundLoading,
  onUnhideAll,
  hiddenBetsCount,
  onOpenHiddenBetsModal,
  evMode,
  onModeChange,
  hasCustomBookieWeights,
  includeEstimatesFo,
  onToggleIncludeEstimates,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const compactBtn = {
    textTransform: "none",
    fontWeight: 500,
    fontSize: "0.78rem",
    borderRadius: 1.5,
    px: 1.5,
    py: 0.5,
    minWidth: "auto",
    borderColor: isDark ? alpha("#ffffff", 0.12) : alpha("#000000", 0.12),
    color: theme.palette.text.secondary,
    "&:hover": {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
      background: alpha(theme.palette.primary.main, 0.08),
    },
  };

  const dividerSx = {
    alignSelf: "stretch",
    my: 0.5,
    borderColor: isDark
      ? alpha("#ffffff", 0.08)
      : alpha("#000000", 0.08),
  };

  return (
    <Box sx={{ width: "100%", mb: 1.5 }}>
      {/* Top row: navigation + refresh controls */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1.5,
        }}
      >
        <Button
          component={Link}
          to="/"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            p: 0,
            minWidth: "auto",
            textTransform: "none",
            color: theme.palette.text.secondary,
            "&:hover": {
              color: theme.palette.primary.main,
              background: "transparent",
            },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: "0.95rem" }} />
          <Typography
            sx={{
              fontSize: "0.85rem",
              fontWeight: 500,
            }}
          >
            Events
          </Typography>
        </Button>

        {/* Refresh group */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Tooltip title="Refresh data">
            <IconButton
              onClick={onRefresh}
              disabled={backgroundLoading}
              size="small"
              sx={{
                color: theme.palette.text.secondary,
                "&:hover": {
                  color: theme.palette.primary.main,
                  background: alpha(theme.palette.primary.main, 0.1),
                },
              }}
            >
              {backgroundLoading ? (
                <CircularProgress size={18} thickness={3} />
              ) : (
                <RefreshIcon sx={{ fontSize: "1.15rem" }} />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title={autoRefresh ? "Auto-refresh ON (1m)" : "Auto-refresh OFF"}>
            <IconButton
              onClick={onAutoRefreshToggle}
              size="small"
              sx={{
                color: autoRefresh
                  ? theme.palette.primary.main
                  : theme.palette.text.secondary,
                background: autoRefresh
                  ? alpha(theme.palette.primary.main, isDark ? 0.2 : 0.12)
                  : "transparent",
                "&:hover": {
                  background: alpha(theme.palette.primary.main, 0.15),
                  color: theme.palette.primary.main,
                },
                ...(autoRefresh && {
                  animation: backgroundLoading
                    ? "none"
                    : "subtle-pulse 2s ease-in-out infinite",
                  "@keyframes subtle-pulse": {
                    "0%, 100%": { opacity: 1 },
                    "50%": { opacity: 0.7 },
                  },
                }),
              }}
            >
              {autoRefresh && backgroundLoading ? (
                <CircularProgress size={18} thickness={3} />
              ) : (
                <AutorenewIcon sx={{ fontSize: "1.15rem" }} />
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Controls bar — single flat row, no sub-sections */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          flexWrap: "wrap",
          px: 1.5,
          py: 1,
          borderRadius: 2,
          background: isDark
            ? alpha("#ffffff", 0.02)
            : alpha("#000000", 0.015),
          border: `1px solid ${isDark ? alpha("#ffffff", 0.06) : alpha("#000000", 0.06)}`,
        }}
      >
        {/* Mode toggle */}
        <ToggleButtonGroup
          value={evMode}
          exclusive
          onChange={onModeChange}
          size="small"
          sx={{
            "& .MuiToggleButton-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.78rem",
              px: 1.5,
              py: 0.35,
              borderColor: isDark
                ? alpha("#ffffff", 0.12)
                : alpha("#000000", 0.12),
              color: theme.palette.text.secondary,
              "&.Mui-selected": {
                background: alpha(theme.palette.primary.main, isDark ? 0.25 : 0.15),
                color: theme.palette.primary.main,
                borderColor: alpha(theme.palette.primary.main, 0.4),
                "&:hover": {
                  background: alpha(theme.palette.primary.main, isDark ? 0.3 : 0.2),
                },
              },
            },
          }}
        >
          <ToggleButton value={true}>Fair Odds</ToggleButton>
          <ToggleButton value={false}>Market</ToggleButton>
        </ToggleButtonGroup>

        {/* Include estimates toggle */}
        <Tooltip
          title={
            includeEstimatesFo
              ? "Estimated odds are included in fair odds calculation"
              : "Estimated odds are excluded from fair odds calculation"
          }
          arrow
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.5,
              cursor: "pointer",
            }}
            onClick={() => onToggleIncludeEstimates(!includeEstimatesFo)}
          >
            <CalculateIcon
              sx={{
                fontSize: "0.9rem",
                color: includeEstimatesFo
                  ? theme.palette.primary.main
                  : theme.palette.text.disabled,
                transition: "color 0.15s ease",
              }}
            />
            <Typography
              sx={{
                fontSize: "0.75rem",
                fontWeight: 500,
                color: includeEstimatesFo
                  ? theme.palette.text.primary
                  : theme.palette.text.disabled,
                whiteSpace: "nowrap",
                userSelect: "none",
                transition: "color 0.15s ease",
              }}
            >
              Est. odds
            </Typography>
            <Switch
              checked={includeEstimatesFo}
              onChange={(e) => onToggleIncludeEstimates(e.target.checked)}
              size="small"
              onClick={(e) => e.stopPropagation()}
              sx={{
                ml: -0.5,
                "& .MuiSwitch-switchBase.Mui-checked": {
                  color: theme.palette.primary.main,
                },
                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                  backgroundColor: theme.palette.primary.main,
                },
              }}
            />
          </Box>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={dividerSx} />

        {/* Settings buttons */}
        <Tooltip title="Kelly criterion settings">
          <Button
            onClick={onOpenKellyModal}
            variant="outlined"
            size="small"
            startIcon={<TuneIcon sx={{ fontSize: "0.9rem !important" }} />}
            sx={compactBtn}
          >
            Kelly
          </Button>
        </Tooltip>

        <Tooltip title="Configure bookie weights for fair odds calculations">
          <Badge
            color="primary"
            variant="dot"
            invisible={!hasCustomBookieWeights}
            sx={{
              "& .MuiBadge-dot": {
                width: 7,
                height: 7,
                minWidth: 7,
              },
            }}
          >
            <Button
              onClick={onOpenBookieWeightsModal}
              variant="outlined"
              size="small"
              startIcon={<ScaleIcon sx={{ fontSize: "0.9rem !important" }} />}
              sx={compactBtn}
            >
              Weights
            </Button>
          </Badge>
        </Tooltip>

        <Divider orientation="vertical" flexItem sx={dividerSx} />

        {/* Hidden bets */}
        <Tooltip title="View hidden bets">
          <Badge
            color="secondary"
            badgeContent={hiddenBetsCount}
            invisible={hiddenBetsCount === 0}
            sx={{
              "& .MuiBadge-badge": {
                fontSize: "0.65rem",
                minWidth: 16,
                height: 16,
              },
            }}
          >
            <Button
              onClick={onOpenHiddenBetsModal}
              variant="outlined"
              size="small"
              startIcon={<VisibilityOffIcon sx={{ fontSize: "0.9rem !important" }} />}
              sx={compactBtn}
            >
              Hidden
            </Button>
          </Badge>
        </Tooltip>
        {hiddenBetsCount > 0 && (
          <Tooltip title={`Unhide all ${hiddenBetsCount} bets`}>
            <IconButton
              onClick={onUnhideAll}
              size="small"
              sx={{
                color: theme.palette.text.secondary,
                "&:hover": {
                  color: theme.palette.warning.main,
                  background: alpha(theme.palette.warning.main, 0.1),
                },
              }}
            >
              <DeleteSweepIcon sx={{ fontSize: "1rem" }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

export default ValuePageHeader;
